import { Component, Inject, inject, OnInit, ChangeDetectionStrategy } from "@angular/core";
import {
    MAT_DIALOG_DATA,
    MatDialogActions,
    MatDialogContent,
    MatDialogRef,
    MatDialogTitle
} from "@angular/material/dialog";
import { QuestionType, Topic, TopicType } from "@teammood/timeline";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from "@angular/forms";
import { AsyncPipe, NgClass } from "@angular/common";
import { BreakpointObserver } from "@angular/cdk/layout";
import { debounceTime, distinctUntilChanged, map, mergeMap, Observable, takeUntil, tap } from "rxjs";
import { TmMoodPickersComponent } from "../../../../shared/ui/moodpickers/mood-pickers.component";
import { QuestionSuggestion, UserActivityService } from "../../../user-activity/user-activity.service";
import { TemplateListComponent } from "../../../timeline-item-creation-dialog/template-list/template-list.component";
import { MatStepperModule } from "@angular/material/stepper";
import { MatButton } from "@angular/material/button";
import { STEPPER_GLOBAL_OPTIONS, StepperOrientation } from "@angular/cdk/stepper";
import { UserService } from "@teammood/shared/util/user";
import { TipService } from "../../tips/tip.service";
import { UserInfos } from "../../../../shared/ui/username-icon/user-infos.type";
import { TopicPreviewComponent } from "../topic-preview/topic-preview.component";
import { TmSlideToggleComponent } from "@teammood/shared/ui/slide-toggle";

@Component({
    selector: 'tm-create-topic-dialog',
    templateUrl: 'create-topic-dialog.component.html',
    styleUrls: ['./../../timeline-item.scss', './create-topic-dialog.component.scss', './../../../timeline-item-creation-dialog/timeline-item-creation-dialog.scss'],
    providers: [
        {
            provide: STEPPER_GLOBAL_OPTIONS,
            useValue: { showError: true },
        },
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        AsyncPipe,
        MatDialogTitle,
        TranslateModule,
        MatDialogContent,
        ReactiveFormsModule,
        TmMoodPickersComponent,
        FormsModule,
        NgClass,
        MatDialogActions,
        TemplateListComponent,
        MatStepperModule,
        TopicPreviewComponent,
        MatButton,
        TmSlideToggleComponent
    ]
})
export class TmCreateTopicDialogComponent implements OnInit {

    protected topic: Topic;
    protected teamId: string;
    protected topicPlaceholder: string;
    protected questionSuggestions: QuestionSuggestion[] = [];
    protected stepperOrientation: Observable<StepperOrientation>;
    protected userInfos: UserInfos;
    protected showIntro: boolean = true;
    protected dontShowAgainCtrl = new FormControl<boolean>(false);
    protected topicMessageCtrl = new FormControl<string>('', [Validators.required, Validators.minLength(1)]);
    protected shouldNotNotifyOnTopicCreation: boolean;
    private topicInfos: TopicInfos;
    protected notifyOnTopicCreationCtrl = new FormControl<boolean>(true, [Validators.required]);

    constructor(
        @Inject(MAT_DIALOG_DATA) protected dialogInput: CreateTopicDialogData,
        private dialogRef: MatDialogRef<TmCreateTopicDialogComponent, TopicInfos>,
        private userActivityService: UserActivityService,
        private translate: TranslateService,
        private userService: UserService,
        private tipService: TipService
    ) {
        const breakpointObserver = inject(BreakpointObserver);
        this.stepperOrientation = breakpointObserver
          .observe('(min-width: 800px)')
          .pipe(map(({matches}) => (matches ? 'horizontal' : 'vertical')));
    }

    ngOnInit() {
        this.teamId = this.dialogInput.teamId;
        this.topic = new Topic(this.dialogInput.type, this.dialogInput.templateId);

        this.topicPlaceholder = this.topic.type === 'question' ? 'question-request-modal.placeholder'
                : this.topic.type === 'issue' ? 'issue-request-modal.placeholder'
                : undefined;

        this.questionSuggestions = this.userActivityService.allQuestionSuggestions();

        if(this.dialogInput.question){
            this.updateMessage(this.dialogInput.question) 
        }

        this.userService.get().then(async (user) => {
            if (user) {
                this.userInfos = new UserInfos(user.firstname, user.lastname);
                this.shouldNotNotifyOnTopicCreation = user.featureTags.find(feat => feat.id === 'NoNotificationOnTopicCreationFeature') !== undefined;
                switch(this.topic.type){
                    case('issue'): 
                        this.showIntro = !user.dismissedTips.includes('topic-issue-intro-tips');
                        break;
                    case('question'): 
                        this.showIntro = !user.dismissedTips.includes('topic-question-intro-tips');
                        break;
                }
            }
        })

        this.topicMessageCtrl.valueChanges.pipe(
            debounceTime(500),
            distinctUntilChanged(),
            tap(() => {
                this.topic.title = {
                    body: this.topicMessageCtrl.getRawValue(),
                    linkPreviews: []
                };
            }),
        ).subscribe();

        this.dontShowAgainCtrl.valueChanges.pipe(
            distinctUntilChanged(),
            mergeMap(dontShowAgain => {
                const topicType = this.topic.type === 'issue' ? 'topic-issue-intro-tips' : 'topic-question-intro-tips';
                if (dontShowAgain) {
                    return this.tipService.dismissTipsById(topicType);
                } else {
                    return this.tipService.restoreTips(topicType);
                }
            }),
            takeUntil(this.dialogRef.afterClosed())
        ).subscribe();
    }

    protected updateMessage(content: string){
        this.topicMessageCtrl.setValue(content);
    }

    protected fromMoodPicker(emojiOrGif: any){
        this.topicMessageCtrl.setValue(this.topic.title.body + emojiOrGif);
    }

    protected updateQuestionType(questionType: QuestionType) {
        this.topic.questionType = questionType;
    }

    protected updatePlaceholder(placeholder: string){
        this.topicMessageCtrl.setValue(placeholder);
    }

    protected questions(): string[] {
        if (this.topic.questionType){
            const question = this.questionSuggestions.find(qs => qs.type === this.topic.questionType);
            if (question) {
                return question.placeholders.map(translationKey => this.translate.instant(translationKey));
            }
        } else {
            return [];
        }
    }

    private setFocus(targetInput: string) {
        setTimeout(function waitTargetElem() {
            let targetElem = document.getElementById(targetInput);
          if (document.body.contains(targetElem)) {
            targetElem.focus();
          } else {
            setTimeout(waitTargetElem, 100);
          }
        }, 300);
      }
    
      onChange(event: any) {
        let index = event.selectedIndex;
        if(index === 1){
            this.setFocus('topic-input');
        }
      }

    protected onClose() {
        this.dialogRef.close();
    }

    protected createTopic(){
        this.topicInfos = {
            topic: this.topic,
            notify: this.notifyOnTopicCreationCtrl.value
        }
        this.dialogRef.close(this.topicInfos);
    }
}

interface CreateTopicDialogData {
    teamId: string,
    type: TopicType
    question: string,
    templateId: string,
}

interface TopicInfos {
    topic: Topic,
    notify: boolean
}